<template>
  <section-card
    data-test="my-env-section-kpis"
    :data-item-count="displayed.length"
    :title="embedded ? t('mijn_omgeving.kpis.title') : null"
    :subtitle="embedded ? t('mijn_omgeving.kpis.subtitle') : null"
    :is-empty="!hasItems"
    :is-loading="isLoading && !hasItems"
    :empty-title="t('mijn_omgeving.kpis.empty_title')"
    :empty-message="t('mijn_omgeving.kpis.empty_message')"
    :compact="snapshot"
  >
    <!-- Layout-matched skeleton for both snapshot and full mode. -->
    <template #loadingSkeleton>
      <ul
        v-if="snapshot"
        class="divide-y divide-theme-border dark:divide-theme-border -my-1"
        aria-hidden="true"
      >
        <li v-for="i in 3" :key="`kpi-skel-${i}`" class="py-2 space-y-1">
          <div class="flex items-center justify-between gap-2">
            <skeleton-text :width="i === 1 ? '40%' : i === 2 ? '55%' : '45%'" height="11px" />
            <skeleton-text width="38px" height="14px" />
          </div>
          <div class="flex items-baseline justify-between gap-2">
            <skeleton-text width="60px" height="13px" />
            <skeleton-text width="70px" height="10px" />
          </div>
        </li>
      </ul>
      <div
        v-else
        :class="[
          'grid gap-3',
          limit && limit < 4
            ? 'grid-cols-1 sm:grid-cols-2'
            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        ]"
        aria-hidden="true"
      >
        <div
          v-for="i in 3"
          :key="`kpi-fullskel-${i}`"
          class="rounded-theme-md border p-3 space-y-2
            bg-theme-surface border-theme-border dark:bg-theme-surface dark:border-theme-border"
        >
          <skeleton-text width="50%" height="11px" />
          <skeleton-text width="65%" height="22px" />
          <skeleton-text width="40%" height="10px" />
        </div>
      </div>
    </template>

    <!-- Snapshot mode: two-line compact rows linking to each KPI's deep link. -->
    <ul v-if="snapshot" class="divide-y divide-theme-border dark:divide-theme-border -my-1">
      <li v-for="row in displayed" :key="row.id" class="min-w-0">
        <component
          :is="row.deepLink ? 'router-link' : 'div'"
          :to="row.deepLink || undefined"
          class="block py-2 -mx-2 px-2 rounded-md transition-colors hover:bg-theme-palette-emerald/40 dark:hover:bg-theme-palette-emerald/15 group"
        >
          <header class="flex items-baseline justify-between gap-2 min-w-0 mb-0.5">
            <span class="text-xs text-theme-text-secondary truncate min-w-0 flex-1">
              {{ row.label || row.id }}
            </span>
            <span
              v-if="!row.loading && row.kpiValue && Number.isFinite(row.kpiValue.change_pct)"
              :class="[
                'shrink-0 inline-flex items-center text-[10px] font-medium tabular-nums px-1.5 py-0.5 rounded-full',
                deltaPillClass(row.kpiValue.change_pct),
              ]"
            >
              {{ formatDeltaPct(row.kpiValue.change_pct) }}
            </span>
          </header>
          <div class="flex items-baseline justify-between gap-2 min-w-0">
            <skeleton-text v-if="row.loading" width="48px" height="14px" class="shrink-0" />
            <span
              v-else-if="
                row.kpiValue && row.kpiValue.value !== null && row.kpiValue.value !== undefined
              "
              class="text-sm font-semibold tabular-nums text-theme-text shrink-0"
            >
              {{ formatKpiValue(row.kpiValue) }}
            </span>
            <span v-else class="text-xs text-theme-text-muted shrink-0">—</span>
            <span
              v-if="!row.loading && row.kpiValue && referenceLabel(row.kpiValue)"
              class="text-[11px] text-theme-text-muted truncate min-w-0"
            >
              {{ referenceLabel(row.kpiValue) }}
            </span>
          </div>
        </component>
      </li>
    </ul>

    <!--
      Full mode: same unified KpiCard as the home Pulse strip. When `manageable`
      (the standalone /my-environment/kpis page) each card gets a drag handle and
      a delete button, and the first 4 are flagged as the home-strip set.
    -->
    <transition-group
      v-else
      tag="div"
      name="kpi-reorder"
      :class="[
        'grid gap-3',
        limit && limit < 4
          ? 'grid-cols-1 sm:grid-cols-2'
          : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      ]"
    >
      <div
        v-for="(row, index) in renderRows"
        :key="row.id"
        class="relative group/kpi kpi-reorder-item"
        data-test="my-kpi-card"
        :data-kpi-id="row.id"
        :data-kpi-origin="row.origin"
        :draggable="manageable ? 'true' : null"
        :class="[
          manageable ? (index === dragIndex ? 'cursor-grabbing' : 'cursor-grab') : null,
          { 'kpi-reorder-item--dragging': index === dragIndex },
        ]"
        @dragstart="manageable && onDragStart(index, $event)"
        @dragenter.prevent="manageable && onDragEnter(index)"
        @dragover.prevent
        @drop.prevent="manageable && onDrop()"
        @dragend="onDragEnd"
      >
        <div v-if="manageable" class="absolute right-2 top-2 z-10 flex items-center gap-1
          opacity-0 transition-opacity group-hover/kpi:opacity-100 focus-within:opacity-100">
          <span
            class="inline-flex h-6 w-6 items-center justify-center rounded text-theme-text-muted cursor-grab"
            :title="t('mijn_omgeving.kpis.drag_hint')"
            aria-hidden="true"
          >
            <icon :path="mdiDragVertical" size="16" />
          </span>
          <button
            v-if="row.canEdit"
            type="button"
            class="inline-flex h-6 w-6 items-center justify-center rounded text-theme-text-muted
              hover:bg-theme-palette-emerald/10 hover:text-theme-palette-emerald"
            data-test="kpi-edit"
            :aria-label="t('mijn_omgeving.kpis.edit_aria', { name: row.label })"
            @click="onEdit(row)"
          >
            <icon :path="mdiPencilOutline" size="16" />
          </button>
          <button
            type="button"
            class="inline-flex h-6 w-6 items-center justify-center rounded text-theme-text-muted
              hover:bg-theme-palette-rose/10 hover:text-theme-palette-rose"
            data-test="kpi-delete"
            :aria-label="t('mijn_omgeving.kpis.delete_aria', { name: row.label })"
            @click="onDelete(row)"
          >
            <icon :path="mdiClose" size="16" />
          </button>
        </div>

        <kpi-card
          :kpi-value="row.kpiValue"
          :label="row.label || row.id"
          :error="row.error"
          :to="row.deepLink || null"
          density="compact"
        />
      </div>
    </transition-group>

    <template v-if="snapshot && hasItems" #footer>
      <router-link
        to="/my-environment/kpis"
        class="inline-flex items-center gap-1 text-xs font-medium
          text-theme-palette-emerald hover:text-theme-palette-emerald
          dark:text-theme-palette-emerald dark:hover:text-theme-palette-emerald"
      >
        <span>{{ t('mijn_omgeving.kpis.view_all') }}</span>
        <icon :path="mdiArrowRight" size="12" />
      </router-link>
    </template>

    <template v-else-if="manageable && hasItems" #footer>
      <p class="text-xs text-theme-text-secondary">
        {{ t('mijn_omgeving.kpis.home_strip_hint') }}
      </p>
    </template>

    <!-- Edit flow: re-open the compose builder pre-filled from the chosen KPI.
         Lives in the default slot (AppDialog teleports, so position is moot) to
         keep a single root node. Only mounted while editing so the lazy builder
         chunk loads on demand. -->
    <kpi-builder-dialog
      v-if="manageable && editOpen"
      :open="editOpen"
      mode="edit"
      :serie="editSerie"
      :dataset-id="editDatasetId"
      :initial-query="editQuery"
      :initial-title="editTitle"
      :save-label="t('mijn_omgeving.kpis.update_kpi')"
      @close="closeEdit"
      @save="onKpiBuilderSave"
    />
  </section-card>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import SectionCard from '@/components/ui/data-display/SectionCard.vue';
  import SkeletonText from '@/components/ui/states/SkeletonText';
  import KpiCard from '@/components/ui/data-display/KpiCard.vue';
  import {
    loadMyKpis,
    loadMyKpisAfterCriticalContent,
    removeKpi,
    reorderKpis,
    updateKpi,
    useMyKpis,
  } from '@/composables/useMyKpis';
  import { useNotification } from '@/composables/useNotification';
  import { mdiArrowRight, mdiClose, mdiDragVertical, mdiPencilOutline } from '@mdi/js';
  import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  // The KPI builder pulls a heavy dependency chain (preview + query engine).
  // Load it lazily — only when the user actually opens the edit dialog — so it
  // stays out of the my-environment initial bundle.
  const KpiBuilderDialog = defineAsyncComponent(
    () => import('@/components/visualizations/kpi/KpiBuilderDialog.vue'),
  );

  const props = defineProps({
    /** When set, render at most this many tiles; null = no limit. Used by Overzicht/Home. */
    limit: { type: Number, default: null },
    /** When true, render the inline label-value row variant for the Home Layer 3 snapshot. */
    snapshot: { type: Boolean, default: false },
    /** When `false`, the section-card hides its title — used by the
     *  standalone `/my-environment/kpis` route where the page-level
     *  StandardToolPage header already identifies the section. */
    embedded: { type: Boolean, default: true },
  });

  const { t, locale } = useI18n();
  const { notifySuccess, notifyError } = useNotification();

  // KPI section reads the unified custom_kpi store (role-based seeds +
  // self-made KPIs). Same payload the home pulse strip renders — single source
  // of truth: deleting/reordering here reflects everywhere.
  const { kpis, isLoading } = useMyKpis();

  // The home pulse strip shows the first 4. We flag them on the standalone page
  // so the user knows which KPIs surface on the homepage.
  const HOME_STRIP_SIZE = 4;

  // Management (drag-reorder + delete) only on the dedicated full page — not in
  // the home snapshot or the limited Overzicht preview.
  const manageable = computed(() => props.embedded === false && !props.snapshot && props.limit === null);

  const displayed = computed(() => {
    const arr = kpis.value || [];
    const slice = props.limit ? arr.slice(0, props.limit) : arr;
    return slice.map((kpi) => ({
      id: kpi.id,
      label: kpi.title,
      origin: kpi.origin,
      editable: kpi.editable,
      // Edit needs a re-openable query string. The backend serializes both
      // self-made and role-based seeds to the compose-dialog DSL; guard anyway
      // so a KPI whose query couldn't be serialized just hides the edit button.
      canEdit: kpi.editable && typeof kpi.kpiQuery === 'string' && kpi.kpiQuery.length > 0,
      loading: false,
      kpiValue: kpi.kpiValue || null,
      error: kpi.error || null,
      deepLink: kpi.deepLink || '',
    }));
  });

  const hasItems = computed(() => displayed.value.length > 0);

  // ── Drag-n-drop reorder (native HTML5; no dependency) ──────────────────
  // Live reorder: while dragging we keep a local, mutable copy of the row order
  // (`dragOrder`) and shuffle it on every `dragenter`, so the surrounding cards
  // visibly slide into their new positions (animated by the <transition-group>
  // FLIP move). On drop we persist that order; on a cancelled drag we discard it
  // and fall back to the store order. `renderRows` is the single source the
  // template iterates: the live drag copy when dragging, the store otherwise.
  const dragIndex = ref(null);
  const dragOrder = ref(null);

  const renderRows = computed(() => dragOrder.value ?? displayed.value);

  function onDragStart(index, event) {
    dragIndex.value = index;
    // Snapshot the current order into a mutable copy we can reorder live.
    dragOrder.value = [...displayed.value];
    // A grab-style drag image + move effect for the premium feel.
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  function onDragEnter(targetIndex) {
    const from = dragIndex.value;
    if (from === null || dragOrder.value === null || from === targetIndex) return;
    const arr = dragOrder.value;
    const [moved] = arr.splice(from, 1);
    arr.splice(targetIndex, 0, moved);
    // The dragged card now lives in the target slot — track it there.
    dragIndex.value = targetIndex;
  }

  function onDrop() {
    const order = dragOrder.value;
    const moved = order && dragIndex.value !== null;
    finishDrag();
    if (!moved) return;
    // The store updates optimistically (synchronously) inside reorderKpis, so
    // `displayed` already matches the live order — no flash when dragOrder clears.
    reorderKpis(order.map((r) => r.id)).catch(() =>
      notifyError(t('mijn_omgeving.kpis.reorder_failed')),
    );
  }

  function onDragEnd() {
    // Fires after drop too; if a drop already cleared state this is a no-op.
    finishDrag();
  }

  function finishDrag() {
    dragIndex.value = null;
    dragOrder.value = null;
  }

  // ── Edit (open the KPI builder pre-filled, persist via update) ─────────
  // We host the same KpiBuilderDialog the create funnel uses, but in 'edit'
  // mode seeded from the existing KPI — no navigation to the underlying chart.
  const editOpen = ref(false);
  const editingKpi = ref(null);

  // The builder only needs the dataset id (for series/filter metadata) and the
  // pre-fill query/title; `serie` is just a label fallback for the dialog header.
  const editDatasetId = computed(() =>
    editingKpi.value?.datasetId != null ? String(editingKpi.value.datasetId) : null,
  );
  const editQuery = computed(() =>
    typeof editingKpi.value?.kpiQuery === 'string' ? editingKpi.value.kpiQuery : '',
  );
  const editTitle = computed(() => editingKpi.value?.title || '');
  const editSerie = computed(() =>
    editingKpi.value
      ? { id: editingKpi.value.seriesId || editingKpi.value.id, title: editingKpi.value.title }
      : null,
  );

  function onEdit(row) {
    // displayed rows are a projection; pull the full asset (query, versionToken)
    // from the store by id.
    const kpi = (kpis.value || []).find((k) => k.id === row.id);
    if (!kpi) return;
    editingKpi.value = kpi;
    editOpen.value = true;
  }

  function closeEdit() {
    editOpen.value = false;
    editingKpi.value = null;
  }

  async function onKpiBuilderSave({ query, title } = {}) {
    const kpi = editingKpi.value;
    editOpen.value = false;
    if (!kpi || !query) {
      editingKpi.value = null;
      return;
    }
    try {
      // title=null preserves the existing title server-side; versionToken guards
      // against a concurrent edit (409 → caught, reload restores fresh state).
      await updateKpi(kpi.id, { title, kpiQuery: query, versionToken: kpi.versionToken });
      notifySuccess(t('mijn_omgeving.kpis.updated'));
    } catch {
      notifyError(t('mijn_omgeving.kpis.update_failed'));
    } finally {
      editingKpi.value = null;
    }
  }

  async function onDelete(row) {
    // eslint-disable-next-line no-alert
    if (typeof window !== 'undefined' && !window.confirm(t('mijn_omgeving.kpis.delete_confirm', { name: row.label }))) {
      return;
    }
    try {
      await removeKpi(row.id);
      notifySuccess(t('mijn_omgeving.kpis.deleted'));
    } catch {
      notifyError(t('mijn_omgeving.kpis.delete_failed'));
    }
  }

  // Snapshot value formatter — currency-style units render with the symbol in
  // front of the number (€ 69,80), other units stay behind (7,04 €/MWh).
  function formatKpiValue(kpiValue) {
    const formatted = kpiValue.value.toLocaleString(locale.value, {
      maximumFractionDigits: 2,
    });
    const unit = kpiValue.unit || '';
    if (unit === '€' || unit === 'EUR') return `€ ${formatted}`;
    if (!unit) return formatted;
    return `${formatted} ${unit}`;
  }

  function formatDeltaPct(pct) {
    if (pct == null || !Number.isFinite(pct)) return '';
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
  }
  function deltaPillClass(pct) {
    if (pct == null || !Number.isFinite(pct) || Math.abs(pct) < 0.1) {
      return 'bg-theme-bg-subtle text-theme-text-secondary dark:bg-theme-surface-raised';
    }
    if (pct > 0)
      return 'bg-theme-palette-emerald/10 text-theme-palette-emerald dark:bg-theme-palette-emerald/30 dark:text-theme-palette-emerald';
    return 'bg-theme-palette-rose/10 text-theme-palette-rose dark:bg-theme-palette-rose/30 dark:text-theme-palette-rose';
  }

  function referenceLabel(kpiValue) {
    const offset = kpiValue?.reference_offset;
    if (!offset) {
      return t('mijn_omgeving.kpis.latest');
    }
    const key = {
      '-1d': 'dataset.kpi_card.reference.yesterday',
      '-7d': 'dataset.kpi_card.reference.last_week',
      '-1w': 'dataset.kpi_card.reference.last_week',
      '-30d': 'dataset.kpi_card.reference.last_month',
      '-1M': 'dataset.kpi_card.reference.last_month',
      '-3M': 'dataset.kpi_card.reference.last_3_months',
      '-1y': 'dataset.kpi_card.reference.last_year',
      '-1Y': 'dataset.kpi_card.reference.last_year',
    }[offset];
    return key ? t(key) : `vs ${offset}`;
  }

  let cancelKpisLoad = () => {};

  onMounted(() => {
    // Always refresh on mount so the page reflects the current server state.
    // The store is a module-level singleton shared with the home strip, so an
    // earlier load leaves it warm — but returning here after the create funnel
    // (or a delete/reorder elsewhere) must re-fetch, otherwise a freshly created
    // KPI wouldn't appear until a manual reload. Show the warm data immediately
    // (no skeleton flash) while the refresh resolves.
    // The Home snapshot sits below the pulse strip: it waits for it.
    if (props.snapshot) cancelKpisLoad = loadMyKpisAfterCriticalContent();
    else loadMyKpis();
  });

  onBeforeUnmount(() => cancelKpisLoad());
</script>

<style scoped>
  /* FLIP move: as the live drag order shuffles, the other cards glide to their
     new slots instead of teleporting. Spring-like easing for a premium feel. */
  .kpi-reorder-item {
    transition:
      transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 200ms ease,
      opacity 200ms ease;
    will-change: transform;
  }

  .kpi-reorder-move {
    transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  /* The card being dragged reads as a lifted "source" slot: dimmed, ringed and
     subtly scaled down so the cursor ghost is clearly the live one. */
  .kpi-reorder-item--dragging {
    opacity: 0.55;
    transform: scale(0.97);
    box-shadow: 0 0 0 2px rgb(var(--color-palette-emerald) / 0.55);
    border-radius: var(--radius-md);
    z-index: 5;
  }

  /* Cards entering/leaving the list (create / delete) ease in and out rather
     than popping, keeping the grid feeling cohesive. */
  .kpi-reorder-enter-from,
  .kpi-reorder-leave-to {
    opacity: 0;
    transform: scale(0.95);
  }

  .kpi-reorder-enter-active {
    transition:
      transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 260ms ease;
  }

  /* Leaving cards (delete) fade and collapse in place; once gone the remaining
     cards FLIP-slide to close the gap. Kept in flow to avoid grid-cell width
     issues that absolute positioning would cause. */
  .kpi-reorder-leave-active {
    transition:
      transform 220ms ease,
      opacity 220ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    .kpi-reorder-item,
    .kpi-reorder-move,
    .kpi-reorder-enter-active,
    .kpi-reorder-leave-active {
      transition: none;
    }
  }
</style>
